import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import MobileStickyCta from "@/components/MobileStickyCta";
import EcomHero from "@/components/ecommerce/EcomHero";
import EcomStores from "@/components/ecommerce/EcomStores";
import { EcomExperience, EcomChallenges } from "@/components/ecommerce/EcomProof";
import { EcomServices, EcomOnlineStore, EcomStrategy, EcomMarketplaces } from "@/components/ecommerce/EcomServices";
import { EcomCapabilities, EcomHowItWorks, EcomWhoWeHelp, EcomWhyRekisa } from "@/components/ecommerce/EcomSystem";
import { EcomPricing, EcomFaq, ECOM_FAQS } from "@/components/ecommerce/EcomPricingFaq";
import EcomForm from "@/components/ecommerce/EcomForm";
import { usePageMeta } from "@/lib/meta";
import { useHashScroll } from "@/lib/useHashScroll";

const PAGE_URL = "https://campaigns.rekisa.co.za/ecommerce-solutions";
const ORG_ID = "https://campaigns.rekisa.co.za/#business";
const TITLE = "eCommerce Websites, Strategy & Marketplace Setup | Rekisa";
const DESCRIPTION = "Rekisa helps South African businesses build online stores, develop eCommerce strategies and sell on Amazon, Takealot and Makro. Get an eCommerce plan.";
const OG_IMAGE = "https://campaigns.rekisa.co.za/media/ecommerce-og.jpg";

const SUB_SERVICES = [
  ["eCommerce Website Development", "Online store planning, design and development with product catalogues, checkout, supported payments, delivery configuration and launch testing."],
  ["eCommerce Strategy & Growth", "Business model, product positioning, pricing, channel selection, customer acquisition, conversion optimisation and retention planning."],
  ["Marketplace Enablement", "Marketplace readiness, seller onboarding support, catalogue preparation and listing creation for Amazon, Takealot and Makro. Seller approval is determined by each marketplace."],
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${PAGE_URL}#webpage`,
      "url": PAGE_URL,
      "name": TITLE,
      "description": DESCRIPTION,
      "inLanguage": "en-ZA",
      "publisher": { "@id": ORG_ID },
      "about": { "@id": `${PAGE_URL}#service` },
      "primaryImageOfPage": OG_IMAGE,
    },
    {
      "@type": "Service",
      "@id": `${PAGE_URL}#service`,
      "name": "Rekisa eCommerce Solutions",
      "serviceType": "eCommerce website development, eCommerce strategy and marketplace enablement",
      "url": PAGE_URL,
      "provider": { "@id": ORG_ID },
      "areaServed": { "@type": "Country", "name": "South Africa" },
      "audience": { "@type": "BusinessAudience", "name": "South African SMEs, retailers, product brands, entrepreneurs and marketplace sellers" },
      "description": "Rekisa helps South African businesses develop eCommerce strategies, build online stores, prepare to sell on marketplaces and improve the customer journey from discovery to repeat purchase. Pricing is provided in a tailored proposal.",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "eCommerce services",
        "itemListElement": SUB_SERVICES.map(([name, description]) => ({ "@type": "Offer", "itemOffered": { "@type": "Service", name, description, "provider": { "@id": ORG_ID } } })),
      },
    },
    {
      "@type": "FAQPage",
      "@id": `${PAGE_URL}#faq`,
      "mainEntity": ECOM_FAQS.map(([q, a]) => ({ "@type": "Question", "name": q, "acceptedAnswer": { "@type": "Answer", "text": a } })),
    },
  ],
};

export default function EcommerceSolutions() {
  usePageMeta({
    title: TITLE,
    description: DESCRIPTION,
    canonical: PAGE_URL,
    ogImage: OG_IMAGE,
    ogImageAlt: "Rekisa eCommerce Solutions: Plan, Build, Sell, Grow — eCommerce websites, strategy and marketplace enablement",
    jsonLd: JSON_LD,
  });
  useHashScroll();

  return (
    <div className="relative min-h-screen bg-white text-zinc-900" data-testid="ecommerce-solutions-page">
      <Header />
      <main>
        <EcomHero />
        <EcomStores />
        <EcomExperience />
        <EcomChallenges />
        <EcomServices />
        <EcomOnlineStore />
        <EcomStrategy />
        <EcomMarketplaces />
        <EcomCapabilities />
        <EcomHowItWorks />
        <EcomWhoWeHelp />
        <EcomWhyRekisa />
        <EcomPricing />
        <EcomFaq />
        <EcomForm />
      </main>
      <Footer />
      <MobileStickyCta label="Get My eCommerce Plan" targetId="request-quote" heroId="home" service="ecommerce_solutions" className="bg-[#E8562A] text-white" />
    </div>
  );
}
